import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import { categories, getVideosByCategory, getCategoryBySlug } from "@/mocks/videos";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const videos = slug ? getVideosByCategory(slug) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500">分类不存在</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = category.name;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${categoryLabel} - 99视频`,
    description: `99视频${categoryLabel}分类，聚合香蕉视频、红桃视频、91视频、蜜桃视频等平台的${categoryLabel}内容，免费高清在线观看。`,
    url: `https://99video.com/category/${slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "99视频",
      url: "https://99video.com",
    },
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: categoryLabel },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6" aria-label={`${categoryLabel}分类页面`}>
          <header className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
              <strong>{categoryLabel}</strong>
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{category.description}</p>
          </header>

          <nav className="flex flex-wrap gap-2 mb-8" aria-label="分类切换">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => {
                  navigate(`/category/${cat.slug}`);
                }}
                className={`px-4 py-2 text-sm rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? "bg-primary-500 text-white border-primary-500"
                    : "bg-background-50 text-foreground-600 border-background-300 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={`${cat.icon} mr-1`}></i>
                {cat.name}
              </button>
            ))}
          </nav>

          {videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
              {videos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-video-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">该分类暂无内容</p>
              <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页查看更多</Link>
            </div>
          )}
        </section>

        <section className="w-full px-4 md:px-6 py-10 bg-background-100" aria-label="相关分类">
          <h2 className="text-lg font-bold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
            浏览更多分类
          </h2>
          <div className="flex flex-wrap gap-3">
            {categories
              .filter((c) => c.slug !== slug)
              .map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-600 hover:border-primary-200 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className={`${cat.icon}`}></i>
                  {cat.name}
                </Link>
              ))}
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </>
  );
}